import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import {
  Upload, Sparkles, Save, FileText, Loader2, Check, X as XIcon,
  ArrowRight, Wand2, AlertTriangle, ChevronLeft,
} from 'lucide-react';

/**
 * Template Wizard MVP (Feb 2026).
 *
 * 5 steps in a single panel:
 *   1) Upload sample file (or load from a statement_id).
 *   2) Inspect detected headers + system-proposed canonical mapping
 *      (with per-column confidence). The accountant confirms / edits.
 *   3) Knobs: amount_rule · debit_is_negative · date_format · separators · currency.
 *   4) Live Preview — call /api/bank-recon/preview with the draft template.
 *   5) Save draft → Test → Publish.
 */

const CANONICAL_LABELS = {
  date:           'التاريخ',
  description:    'البَيان',
  debit:          'مدين',
  credit:         'دائن',
  amount:         'المَبلَغ (مُوقَّع)',
  balance:        'الرَّصيد',
  reference:      'المَرجِع',
  counterparty:   'الطَرَف الآخَر',
  cheque_no:      'رَقم الشيك',
  iban:           'IBAN',
  currency:       'العُملَة',
  sadad_number:   'رَقم سَداد',
  invoice_number: 'رَقم الفاتورَة',
};

const CANONICALS = Object.keys(CANONICAL_LABELS);


export default function BankTemplateWizard() {
  const [params] = useSearchParams();
  const stmtIdFromUrl = params.get('stmt_id');

  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [analyzed, setAnalyzed] = useState(null);   // {headers, sample_rows, suggestions}
  const [mapping, setMapping] = useState({});       // header → canonical
  const [knobs, setKnobs] = useState({
    name: 'قالِب جَديد',
    template_key: '',
    bank_code: '',
    amount_rule: 'debit_credit',
    debit_is_negative: true,
    date_format: '',
    decimal_sep: '.', thousand_sep: ',',
    currency_default: 'SAR',
  });
  const [preview, setPreview] = useState(null);
  const [draftId, setDraftId] = useState(null);

  const fileRef = useRef(null);

  // Auto-load from statement if stmt_id was passed.
  useEffect(() => {
    if (!stmtIdFromUrl) return;
    (async () => {
      try {
        const { data } = await axiosInstance.get(`/bank-recon/${stmtIdFromUrl}/parse-log`);
        if (data.template_key) setKnobs(k => ({ ...k,
          name: `${data.template_key} (نُسخَة مُعَدَّلَة)`,
          template_key: `${data.template_key}_v${(data.template_version || 1) + 1}`,
        }));
      } catch (e) { /* parse-log fetch is best-effort */ }
    })();
  }, [stmtIdFromUrl]);

  const analyze = async (file) => {
    setBusy(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const { data } = await axiosInstance.post('/bank-recon/analyze', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setAnalyzed(data);
      // Pre-fill mapping from suggestions ≥ 0.7.
      const init = {};
      (data.suggestions || []).forEach(s => {
        if (s.canonical && s.confidence >= 0.7) init[s.header] = s.canonical;
      });
      setMapping(init);
      setStep(2);
      toast.success(`تَم تَحليل ${data.headers.length} عَمود`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'فَشَل التَّحليل');
    } finally { setBusy(false); }
  };

  // Build column_aliases dict the backend mapper expects.
  const draftTemplate = useMemo(() => {
    const column_aliases = {};
    Object.entries(mapping).forEach(([header, canon]) => {
      if (!canon) return;
      column_aliases[canon] = column_aliases[canon] || [];
      column_aliases[canon].push(header);
    });
    return { ...knobs, column_aliases };
  }, [knobs, mapping]);

  const runPreview = async () => {
    if (!analyzed) return;
    setBusy(true);
    try {
      const { data } = await axiosInstance.post('/bank-recon/preview', {
        headers: analyzed.headers,
        rows: analyzed.sample_rows,
        template: draftTemplate,
      });
      setPreview(data);
      setStep(4);
    } catch (e) {
      toast.error('فَشَلَت المُعايَنَة');
    } finally { setBusy(false); }
  };

  const saveDraft = async () => {
    if (!knobs.template_key.trim()) { toast.error('أَدخِل المُعَرِّف (template_key)'); return; }
    setBusy(true);
    try {
      const payload = {
        ...draftTemplate,
        template_key: knobs.template_key.trim(),
        detect_fingerprints: [], hints: {}, enabled: true,
        skip_rows: 0,
      };
      if (draftId) {
        await axiosInstance.patch(`/bank-mapping-templates/${draftId}`, payload);
        toast.success('تَم تَحديث المُسَوَّدَة');
      } else {
        const { data } = await axiosInstance.post('/bank-mapping-templates', payload);
        setDraftId(data.id);
        toast.success('تَم حِفظ القالِب');
      }
    } catch (e) {
      const det = e?.response?.data?.detail;
      toast.error(typeof det === 'string' ? det : 'فَشَل الحِفظ');
    } finally { setBusy(false); }
  };

  return (
    <div className="p-4 max-w-6xl mx-auto" dir="rtl">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div className="flex items-center gap-3">
          <button onClick={() => window.history.back()}
                  data-testid="wizard-back"
                  className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900">
            <ChevronLeft className="w-4 h-4" /> العَودة
          </button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Wand2 className="w-6 h-6 text-indigo-600" />
              Template Wizard — مُنشِئ القَوالِب البَنكية
            </h1>
            <p className="text-sm text-slate-500 mt-0.5">
              ارفع ملف عَيِّنَة، أَكِّد المُطابَقة، عاين النَتيجَة، احفَظ كَقالِب.
            </p>
          </div>
        </div>
      </div>

      {/* Stepper */}
      <div className="flex items-center justify-between mb-6">
        {[
          { n: 1, label: 'رَفع' },
          { n: 2, label: 'مُطابَقة' },
          { n: 3, label: 'إعدادات' },
          { n: 4, label: 'مُعايَنَة' },
          { n: 5, label: 'حِفظ' },
        ].map((s, i, arr) => (
          <React.Fragment key={s.n}>
            <div className={`flex items-center gap-2 ${step >= s.n ? 'text-indigo-700' : 'text-slate-400'}`}>
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border-2
                ${step >= s.n ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white border-slate-300'}`}
                   data-testid={`step-${s.n}`}>
                {step > s.n ? '✓' : s.n}
              </div>
              <div className="text-sm font-semibold">{s.label}</div>
            </div>
            {i < arr.length - 1 && <div className={`h-0.5 flex-1 mx-2 ${step > s.n ? 'bg-indigo-600' : 'bg-slate-200'}`} />}
          </React.Fragment>
        ))}
      </div>

      {/* Step 1: Upload */}
      {step === 1 && (
        <div className="rounded-xl border bg-white p-8 text-center" data-testid="step-1-panel">
          <Upload className="w-12 h-12 mx-auto text-indigo-600 mb-3" />
          <h2 className="font-bold text-lg mb-1">ارفع ملف عَيِّنَة</h2>
          <p className="text-sm text-slate-500 mb-4">CSV، Excel، أَو PDF. سَيَستَخرِج النِظام العَناوين وَيَقتَرِح المُطابَقة.</p>
          <input ref={fileRef} type="file"
                 accept=".csv,.xlsx,.xlsm,.pdf"
                 onChange={(e) => { const f = e.target.files?.[0]; if (f) analyze(f); }}
                 className="hidden" data-testid="wizard-file-input" />
          <button onClick={() => fileRef.current?.click()}
                  data-testid="wizard-upload-btn"
                  disabled={busy}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white text-sm font-semibold px-4 py-2">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            اختَر ملفاً
          </button>
        </div>
      )}

      {/* Step 2: Mapping */}
      {step >= 2 && analyzed && (
        <div className="rounded-xl border bg-white p-4 mb-4" data-testid="step-2-panel">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-bold">مُطابَقة الأَعمِدَة (الـ Confidence Trainer)</h2>
            <span className="text-xs text-slate-500">{analyzed.headers.length} عَمود · {analyzed.reader_kind}</span>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-[11px] uppercase text-slate-500">
                <tr>
                  <th className="text-start px-3 py-2">عَمود الملَف</th>
                  <th className="text-start px-3 py-2">المُقتَرَح من النِظام</th>
                  <th className="text-start px-3 py-2">المُطابَقة (تَأكيد المُحاسِب)</th>
                  <th className="text-start px-3 py-2">عَيِّنَة من البَيانات</th>
                </tr>
              </thead>
              <tbody>
                {analyzed.headers.map((h, i) => {
                  const sug = (analyzed.suggestions || []).find(x => x.header === h) || {};
                  const conf = sug.confidence || 0;
                  const confCls = conf >= 0.9 ? 'text-emerald-700 bg-emerald-50 border-emerald-200'
                    : conf >= 0.7 ? 'text-amber-700 bg-amber-50 border-amber-200'
                    : 'text-slate-500 bg-slate-50 border-slate-200';
                  const sample = analyzed.sample_rows.map(r => r[i]).filter(v => v !== null && v !== undefined && v !== '').slice(0, 2);
                  return (
                    <tr key={h} className="border-t">
                      <td className="px-3 py-2 font-mono">{h}</td>
                      <td className="px-3 py-2">
                        {sug.canonical ? (
                          <span className={`inline-flex items-center gap-1 text-[11px] font-semibold border rounded-full px-2 py-0.5 ${confCls}`}>
                            {CANONICAL_LABELS[sug.canonical]} · {Math.round(conf * 100)}%
                          </span>
                        ) : (
                          <span className="text-xs text-slate-400">غَير مَعروف</span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <select value={mapping[h] || ''}
                                onChange={(e) => setMapping(m => ({ ...m, [h]: e.target.value }))}
                                data-testid={`map-${i}`}
                                className="rounded-lg border border-slate-300 px-2 py-1 text-sm bg-white min-w-[140px]">
                          <option value="">— تَجاهُل —</option>
                          {CANONICALS.map(c => (
                            <option key={c} value={c}>{CANONICAL_LABELS[c]}</option>
                          ))}
                        </select>
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-500 font-mono">
                        {sample.length === 0 ? '—' : sample.map(s => String(s).slice(0, 30)).join(' · ')}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="mt-3 flex justify-end">
            <button onClick={() => setStep(3)}
                    data-testid="goto-step-3"
                    className="rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold px-3 py-1.5">
              التالي: إعدادات →
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Knobs */}
      {step >= 3 && (
        <div className="rounded-xl border bg-white p-4 mb-4" data-testid="step-3-panel">
          <h2 className="font-bold mb-3">إعدادات القالِب</h2>
          <div className="grid md:grid-cols-2 gap-3 text-sm">
            <div>
              <label className="text-xs font-semibold text-slate-600">الاسم</label>
              <input value={knobs.name} onChange={(e) => setKnobs(k => ({ ...k, name: e.target.value }))}
                     data-testid="knob-name"
                     className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600">المُعَرِّف (template_key)</label>
              <input value={knobs.template_key} onChange={(e) => setKnobs(k => ({ ...k, template_key: e.target.value }))}
                     data-testid="knob-key"
                     placeholder="sa_mybank_v1"
                     className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 font-mono text-xs" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600">نَوع المَبلَغ</label>
              <select value={knobs.amount_rule} onChange={(e) => setKnobs(k => ({ ...k, amount_rule: e.target.value }))}
                      data-testid="knob-amount-rule"
                      className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 bg-white">
                <option value="debit_credit">مدين/دائن (عَمودان)</option>
                <option value="signed_amount">مُوقَّع (عَمود واحِد)</option>
                <option value="amount_with_dc">مَبلَغ + مُؤَشِّر D/C</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600">المدين سالِب؟</label>
              <select value={knobs.debit_is_negative ? '1' : '0'}
                      onChange={(e) => setKnobs(k => ({ ...k, debit_is_negative: e.target.value === '1' }))}
                      data-testid="knob-debit-sign"
                      className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 bg-white">
                <option value="1">نَعَم (مدين → سالِب)</option>
                <option value="0">لا (مدين → موجَب)</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600">تَنسيق التاريخ (اختِياري)</label>
              <input value={knobs.date_format} onChange={(e) => setKnobs(k => ({ ...k, date_format: e.target.value }))}
                     data-testid="knob-date-fmt"
                     placeholder="%d/%m/%Y" className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 font-mono text-xs" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600">العُملَة الافتِراضية</label>
              <input value={knobs.currency_default} onChange={(e) => setKnobs(k => ({ ...k, currency_default: e.target.value.toUpperCase() }))}
                     className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 font-mono" />
            </div>
          </div>
          <div className="mt-3 flex justify-end gap-2">
            <button onClick={() => setStep(2)} className="rounded-lg border border-slate-300 hover:bg-slate-50 text-sm px-3 py-1.5">← السابِق</button>
            <button onClick={runPreview} disabled={busy}
                    data-testid="goto-step-4"
                    className="rounded-lg bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white text-sm font-semibold px-3 py-1.5">
              {busy ? <Loader2 className="w-4 h-4 animate-spin inline" /> : <Sparkles className="w-4 h-4 inline" />} مُعايَنَة →
            </button>
          </div>
        </div>
      )}

      {/* Step 4: Live Preview */}
      {step >= 4 && preview && (
        <div className="rounded-xl border bg-white p-4 mb-4" data-testid="step-4-panel">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-bold">مُعايَنَة مُباشِرَة</h2>
            {preview.validation && (() => {
              const c = preview.validation.confidence;
              const cls = c >= 0.95 ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                : c >= 0.8 ? 'bg-amber-50 text-amber-800 border-amber-200'
                : 'bg-red-50 text-red-800 border-red-200';
              return (
                <span className={`inline-flex items-center gap-1 text-xs font-semibold border rounded-full px-2 py-0.5 ${cls}`}>
                  {c >= 0.95 ? '✓' : c >= 0.8 ? '⚠' : '✗'} جودَة {Math.round(c * 100)}%
                </span>
              );
            })()}
          </div>
          {preview.warnings?.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-800 mb-2">
              <AlertTriangle className="w-3 h-3 inline me-1" />
              {preview.warnings.slice(0, 3).join(' · ')}
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
                <tr>
                  <th className="text-start px-2 py-1">#</th>
                  <th className="text-start px-2 py-1">التاريخ</th>
                  <th className="text-start px-2 py-1">البَيان</th>
                  <th className="text-end px-2 py-1">المَبلَغ</th>
                  <th className="text-end px-2 py-1">الرَّصيد</th>
                  <th className="text-start px-2 py-1">المَرجِع</th>
                </tr>
              </thead>
              <tbody>
                {preview.lines?.length ? preview.lines.map((l, i) => (
                  <tr key={i} className="border-t">
                    <td className="px-2 py-1 text-slate-400">{l.line_no}</td>
                    <td className="px-2 py-1 font-mono">{l.date}</td>
                    <td className="px-2 py-1">{l.description}</td>
                    <td className={`px-2 py-1 text-end font-mono ${l.amount < 0 ? 'text-red-600' : 'text-emerald-700'}`}>
                      {Number(l.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </td>
                    <td className="px-2 py-1 text-end font-mono">{l.balance_after != null ? Number(l.balance_after).toLocaleString(undefined, { minimumFractionDigits: 2 }) : '—'}</td>
                    <td className="px-2 py-1 font-mono text-slate-500">{l.reference || '—'}</td>
                  </tr>
                )) : (
                  <tr><td colSpan={6} className="p-8 text-center text-slate-400">لا توجَد أَسطُر — راجِع المُطابَقة.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="mt-3 flex justify-end gap-2">
            <button onClick={() => setStep(3)} className="rounded-lg border border-slate-300 hover:bg-slate-50 text-sm px-3 py-1.5">← السابِق</button>
            <button onClick={() => setStep(5)}
                    data-testid="goto-step-5"
                    className="rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold px-3 py-1.5">
              التالي: حِفظ →
            </button>
          </div>
        </div>
      )}

      {/* Step 5: Save / Test / Publish */}
      {step >= 5 && (
        <div className="rounded-xl border bg-white p-4" data-testid="step-5-panel">
          <h2 className="font-bold mb-3">حِفظ القالِب</h2>
          <div className="text-sm text-slate-600 mb-3 space-y-1">
            <div>الاسم: <span className="font-semibold">{knobs.name}</span></div>
            <div>المُعَرِّف: <span className="font-mono text-xs">{knobs.template_key || '—'}</span></div>
            <div>الأَعمِدَة المُرتَبِطَة: <span className="font-semibold">{Object.values(mapping).filter(Boolean).length}</span></div>
            <div>جودَة المُعايَنَة: <span className="font-mono">{preview ? Math.round((preview.validation?.confidence || 0) * 100) : '—'}%</span></div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={saveDraft} disabled={busy || !knobs.template_key.trim()}
                    data-testid="save-template-btn"
                    className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-semibold px-3 py-2">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {draftId ? 'تَحديث المُسَوَّدَة' : 'حِفظ كَمُسَوَّدَة'}
            </button>
            {draftId && (
              <span className="inline-flex items-center text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-1"
                    data-testid="draft-saved">
                ✓ مَحفوظ — يَظهَر في قائِمَة القَوالِب الآن
              </span>
            )}
          </div>
          <div className="mt-3 text-[11px] text-slate-500">
            ملاحظَة: في إصدار الـ MVP، الحِفظ يُنشِئ القالِب مُباشَرَةً في القَوالِب النَّشِطَة (مَع versioning).
            Publish workflow الكامِل + Version Compare قادِمان في Wave 2-C.
          </div>
        </div>
      )}
    </div>
  );
}
